import { formDefinition, EVALUATION_FORMS } from './evaluation-forms';

export const EVALUATION_FORM_TYPES = EVALUATION_FORMS.map(form => ({
  value: form.value,
  label: form.label,
  roles: form.roles
}));

export const EVALUATION_STATUSES = [
  { value: 'draft', label: 'Draft', badge: 'bg-secondary' },
  { value: 'submitted', label: 'Submitted', badge: 'bg-primary' },
  { value: 'edit_requested', label: 'Edit requested', badge: 'bg-warning text-dark' },
  { value: 'approved', label: 'Approved', badge: 'bg-success' },
  { value: 'rejected', label: 'Rejected', badge: 'bg-danger' }
] as const;

export type EvaluationFormType = 'housekeeping' | 'physiotherapy' | 'receptionist';
export type EvaluationStatus = typeof EVALUATION_STATUSES[number]['value'];
export type EvaluationFieldType = 'text' | 'textarea' | 'date' | 'number' | 'select' | 'rating';

export interface EvaluationFieldOption {
  value: string | number;
  label: string;
}

export interface EvaluationField {
  key: string;
  label: string;
  type: EvaluationFieldType;
  required?: boolean;
  section?: string;
  hint?: string;
  bullets?: string[];
  options?: EvaluationFieldOption[];
}

export interface Evaluation {
  id: string;
  employee_id: string;
  employee_name: string;
  form_type: string;
  period_from: string | null;
  period_to: string | null;
  answers: Record<string, string | number | null>;
  employee_notes: string | null;
  admin_comment: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  reviewed_at: string | null;
}

export interface EvaluationWritePayload {
  employee_id?: string;
  employee_name?: string;
  form_type?: string;
  period_from: string | null;
  period_to: string | null;
  answers: Record<string, string | number | null>;
  employee_notes: string | null;
  status?: string;
  edited_by_admin?: boolean;
}

export interface EvaluationReviewPayload {
  action: 'approve' | 'reject' | 'request_edit';
  comment: string | null;
}

export interface EvaluationScore {
  sum: number;
  max: number;
  rated: number;
  total: number;
  complete: boolean;
  percent: number | null;
  percentLabel: string | null;
  rank: string | null;
}

export const APPRAISAL_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const;

export const QUARTERS = [
  { value: 1, label: 'Q1 · Jan – Mar', startMonth: 1, endMonth: 3, endDay: 31 },
  { value: 2, label: 'Q2 · Apr – Jun', startMonth: 4, endMonth: 6, endDay: 30 },
  { value: 3, label: 'Q3 · Jul – Sep', startMonth: 7, endMonth: 9, endDay: 30 },
  { value: 4, label: 'Q4 · Oct – Dec', startMonth: 10, endMonth: 12, endDay: 31 }
] as const;

export function formTypeForRole(role: string | null | undefined): EvaluationFormType | null {
  const normalized = (role ?? '').toLowerCase();
  const match = EVALUATION_FORMS.find(form =>
    (form.roles as readonly string[]).includes(normalized)
  );
  return match ? match.value : null;
}

export function formTypeLabel(type: string | null | undefined): string {
  return formDefinition(type)?.label ?? (type || 'Evaluation');
}

export function statusMeta(status: string | null | undefined): { value: string; label: string; badge: string } {
  return EVALUATION_STATUSES.find(item => item.value === status) ?? {
    value: status ?? '',
    label: status || 'Unknown',
    badge: 'bg-secondary'
  };
}

export function fieldsFor(formType: string | null | undefined): EvaluationField[] {
  return formDefinition(formType)?.fields ?? [];
}

export function missingRequiredFields(
  formType: string | null | undefined,
  answers: Record<string, string | number | null>
): EvaluationField[] {
  return fieldsFor(formType).filter(field => {
    if (!field.required) {
      return false;
    }
    const value = answers?.[field.key];
    return value === null || value === undefined || value === '';
  });
}

export function employeeCanEdit(status: string | null | undefined): boolean {
  return status === 'draft' || status === 'edit_requested';
}

export function toInputDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export function currentQuarter(date = new Date()): { year: number; quarter: number } {
  return {
    year: date.getFullYear(),
    quarter: Math.floor(date.getMonth() / 3) + 1
  };
}

export function quarterRange(year: number, quarter: number): { from: string; to: string } | null {
  const spec = QUARTERS.find(item => item.value === quarter);
  if (!spec || !year) {
    return null;
  }
  const month = (value: number) => String(value).padStart(2, '0');
  return {
    from: `${year}-${month(spec.startMonth)}-01`,
    to: `${year}-${month(spec.endMonth)}-${spec.endDay}`
  };
}

export function quarterFromDates(
  from: string | null | undefined,
  to?: string | null
): { year: number; quarter: number } | null {
  const start = toInputDate(from);
  if (!start) {
    return null;
  }
  const [yearText, monthText] = start.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  if (!year || !month) {
    return null;
  }
  const end = toInputDate(to);
  if (end) {
    const range = quarterRange(year, Math.ceil(month / 3));
    if (!range || range.from !== start || range.to !== end) {
      return { year, quarter: Math.ceil(month / 3) };
    }
  }
  return { year, quarter: Math.ceil(month / 3) };
}

export function quarterLabel(from: string | null | undefined, to?: string | null): string {
  const parsed = quarterFromDates(from, to);
  if (!parsed) {
    return '—';
  }
  const spec = QUARTERS.find(item => item.value === parsed.quarter);
  return `${parsed.year} · ${spec?.label ?? 'Q' + parsed.quarter}`;
}

export function quarterError(year: number | null, quarter: number | null): string | null {
  if (!year || !quarter) {
    return 'Choose the evaluation quarter.';
  }
  if (!QUARTERS.some(item => item.value === quarter)) {
    return 'Choose a quarter from Q1 to Q4.';
  }
  if (year < 2000 || year > 2100) {
    return 'Choose a valid year.';
  }
  return null;
}

export function sameQuarter(
  from: string | null | undefined,
  to: string | null | undefined,
  year: number | null,
  quarter: number | null
): boolean {
  const range = year && quarter ? quarterRange(year, quarter) : null;
  if (!range) {
    return false;
  }
  return toInputDate(from) === range.from && toInputDate(to) === range.to;
}

export function scoreSummary(
  formType: string | null | undefined,
  answers: Record<string, string | number | null> | null | undefined
): EvaluationScore {
  const definition = formDefinition(formType);
  const ratings = (definition?.fields ?? []).filter(field => field.type === 'rating');
  const values = ratings.map(field => ratingNumber(answers?.[field.key]));
  const ratedValues = values.filter((value): value is number => value !== null);
  const sum = ratedValues.reduce((total, value) => total + value, 0);
  const max = ratings.length * (definition?.scaleMax ?? 0);
  const complete = ratings.length > 0 && ratedValues.length === ratings.length;
  const percent = complete && max > 0 ? Math.round((sum / max) * 1000) / 10 : null;
  const rank = percent === null
    ? null
    : [...(definition?.rankings ?? [])].sort((a, b) => b.min - a.min).find(band => percent >= band.min)?.label ?? null;

  return {
    sum,
    max,
    rated: ratedValues.length,
    total: ratings.length,
    complete,
    percent,
    percentLabel: percent === null ? null : formatPercent(percent),
    rank
  };
}

export function resultLabel(
  formType: string | null | undefined,
  answers: Record<string, string | number | null> | null | undefined
): string {
  const score = scoreSummary(formType, answers);
  if (!score.complete || score.percentLabel === null) {
    return score.total ? `${score.rated}/${score.total}` : '—';
  }
  return `${score.percentLabel}% · ${score.rank ?? ''}`.trim();
}

export function isMissingEvaluationApi(error: { status?: number } | null | undefined): boolean {
  // 0 happens when the missing route returns a non-CORS 404, so the browser hides the status.
  return !error?.status || error.status === 404 || error.status === 501;
}

function ratingNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatPercent(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function asAnswers(value: unknown): Record<string, string | number | null> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, string | number | null>;
}

export function normalizeEvaluation(row: any): Evaluation | null {
  if (!row) {
    return null;
  }
  const item = row.evaluation ?? row;
  if (!item?.id) {
    return null;
  }
  return {
    id: String(item.id),
    employee_id: String(item.employee_id ?? ''),
    employee_name: item.employee_name ?? item.employee?.name ?? item.name ?? '',
    form_type: item.form_type ?? '',
    period_from: item.period_from ?? null,
    period_to: item.period_to ?? null,
    answers: asAnswers(item.answers),
    employee_notes: item.employee_notes ?? null,
    admin_comment: item.admin_comment ?? item.review_comment ?? null,
    status: item.status ?? 'draft',
    created_at: item.created_at ?? null,
    updated_at: item.updated_at ?? null,
    reviewed_at: item.reviewed_at ?? null
  };
}

export function normalizeEvaluations(data: any): Evaluation[] {
  if (data && !Array.isArray(data) && data.id) {
    const single = normalizeEvaluation(data);
    return single ? [single] : [];
  }
  const rows = Array.isArray(data)
    ? data
    : (data?.evaluations ?? data?.items ?? data?.data ?? []);
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .map(row => normalizeEvaluation(row))
    .filter((item): item is Evaluation => !!item);
}
